import { useOnValueChange } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { Loader } from '@calendly/ui/components/loader'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { Component } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  fetchFilterBootstrap,
  clearFilterBootstrap,
  selectAllFilterItems,
  unselectAllFilterItems,
  clearFiltersError,
  fetchMoreFilterItems,
  fetchMoreFilterGroups
} from 'actions/meetings/filters'
import SearchInput from 'components/core/search_input'
import { useScope } from 'hooks/meetings/use_scope'
import { createFilterSelector, createSelectedIdsSelector } from 'selectors/filter_dropdown_menu'

import { FilterHeader, ItemActionsContainer, ItemsActionButton, StyledMenu, CloseButton } from './styles'

const ItemLabelWithGroup = ({ item, children }) => (
  <>
    {children}
    {(item.group || {}).name && ` (${item.group.name})`}
  </>
)

// TODO: I don't think this should be a class, but instead a functional component
class FilterBodyComponent extends Component {
  // term is search term
  state = { term: null }

  componentDidMount() {
    this.loadData()
  }

  componentWillUnmount() {
    this.clearData()
  }

  onSearchInputChange = (term) => {
    this.setState({ term })
    this.loadData(term)
  }

  onSelectAll = () => {
    this.props.selectAllFilterItems(this.props.name)
  }

  onClearAll = () => {
    this.props.unselectAllFilterItems(this.props.name)
  }

  getItemsActionsClassName() {
    if (!this.isDataReady()) { return 'disabled' }
    return null
  }

  isDataReady() {
    return this.props.status === 'ready'
  }

  loadData(term) {
    this.props.fetchFilterBootstrap(
      this.props.name,
      this.props.scope,
      this.props.params,
      term,
      'none',
      null,
      null,
    )
  }

  clearData() {
    this.props.clearFilterBootstrap(this.props.name)
  }

  loadMoreItems(group, term) {
    this.props.fetchMoreFilterItems(
      'me',
      this.props.name,
      group,
      this.props.scope,
      this.props.params,
      term,
      0
    )
  }

  loadMoreGroups(groups, term) {
    this.props.fetchMoreFilterGroups(
      'me',
      this.props.name,
      groups,
      this.props.scope,
      term
    )
  }


  renderHeader() {
    if (!this.props.showHeader) { return null }

    return (
      <FilterHeader>
        {this.renderSearchInput()}
        {this.renderItemsAction()}
      </FilterHeader>
    )
  }

  renderBody() {
    if (!this.isDataReady()) {
      return (
        <Loader className="mvl" />
      )
    }

    return (
      <>
        <StyledMenu
          filterName={this.props.name}
          onShowMoreItems={(group) => this.loadMoreItems(group, this.state.term)}
          onShowMoreGroups={() => this.loadMoreGroups(this.props.groups, this.state.term)}
          showMoreGroupsLabel={this.props.showMoreGroupsLabel}
          ItemLabel={this.props.showSelectedItemGroup && ItemLabelWithGroup}
        />
        {this.props.isPhone && <CloseButton closeDropdown={this.props.closeDropdown}>{I18n.t('publisher.action.close')}</CloseButton>}
      </>
    )
  }

  renderSearchInput() {
    if (!this.props.showSearchInput) return null

    return (
      <SearchInput
        className="mbm"
        placeholder={I18n.t('publisher.meetings.filter_modal.dropdown.search_placeholder')}
        autofocus
        onChange={this.onSearchInputChange}
      />
    )
  }

  renderItemsAction() {
    return (
      <ItemActionsContainer>
        <ItemsActionButton
          className={this.getItemsActionsClassName()}
          onClick={this.onSelectAll}
        >
          {I18n.t('publisher.dashboard.filters.select_all')}
        </ItemsActionButton>
        <span> / </span>
        <ItemsActionButton
          decoration="minimal"
          fontSize="medium"
          className={this.getItemsActionsClassName()}
          onClick={this.onClearAll}
        >
          {I18n.t('publisher.dashboard.filters.clear')}
        </ItemsActionButton>
      </ItemActionsContainer>
    )
  }

  render() {
    return (
      <>
        {this.renderHeader()}
        {this.renderBody()}
      </>
    )
  }
}

export const FilterBody = ({ name, toggleDropdown, ...props }) => {
  const dispatch = useDispatch()
  const filter = useSelector(createFilterSelector(name))
  const scope = useScope()
  const isPhone = useDeviceType('isPhone')
  const closeDropdown = () => {
    dispatch(clearFiltersError())
    toggleDropdown()
  }

  const formattedScope = {
    type: scope.scopeType,
    id: scope.scopeId
  }

  const selectedIds = useSelector(createSelectedIdsSelector(name))

  useOnValueChange(selectedIds, () => {
    props.onApply(selectedIds)
  })

  return (
    <FilterBodyComponent
      {...props}
      name={name}
      scope={formattedScope}
      status={filter.status}
      groups={filter.groups}
      showSelectedItemGroup={scope.type !== 'team'}
      fetchFilterBootstrap={(...args) => dispatch(fetchFilterBootstrap(...args))}
      clearFilterBootstrap={(...args) => dispatch(clearFilterBootstrap(...args))}
      fetchMoreFilterItems={(...args) => dispatch(fetchMoreFilterItems(...args))}
      fetchMoreFilterGroups={(...args) => dispatch(fetchMoreFilterGroups(...args))}
      selectAllFilterItems={(...args) => dispatch(selectAllFilterItems(...args))}
      unselectAllFilterItems={(...args) => dispatch(unselectAllFilterItems(...args))}
      closeDropdown={closeDropdown}
      isPhone={isPhone}
    />
  )
}
